import React, { useState, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import {
  IconButton,
  Tooltip,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField
} from '@mui/material'
import {
  Close,
  UploadFile,
  Download,
  Delete
} from '@mui/icons-material'

// Store
import { useGlobalStoreData } from '../../../../../store/globalStore'

// Component Imports
import DialogCloseButton from '../atoms/DialogCloseButton'

// Utils
import readExcel from '../../../utils/readExcel'
import { LANGUAGES } from '../../../../../utils/Utils'

const transformToGroupedData = (rows) => {
  const firmMap = new Map()

  rows.forEach((row) => {
    const website = row.website?.trim()
    const companyName = row.companyName?.trim()

    if (!website || !companyName) return

    if (!firmMap.has(website)) {
      firmMap.set(website, {
        name: companyName,
        contactList: [],
        website
      })
    }

    const firm = firmMap.get(website)

    if (row.email?.trim()) {
      firm.contactList.push({
        firstName: row.firstName?.trim() || '',
        lastName: row.lastName?.trim() || '',
        email: row.email.trim()
      })
    }
  })

  return Array.from(firmMap.values())
}

const transformToFlatRows = (groupedData) => {
  const rows = []

  groupedData.forEach((firm) => {
    if (firm.contactList?.length > 0) {
      firm.contactList.forEach((contact, index) => {
        rows.push({
          companyName: firm.name,
          website: index === 0 ? firm.website : '',
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email
        })
      })
    } else {
      rows.push({
        companyName: firm.name,
        website: firm.website,
        firstName: '',
        lastName: '',
        email: ''
      })
    }
  })

  return rows
}

const getValidExcelFirms = (rows) => {
  const grouped = transformToGroupedData(rows)

  const validFirms = grouped?.map((firmObj) => {
    const validContactList = firmObj.contactList?.filter((contact) => contact.email?.trim())

    return ({
      ...firmObj,
      contactList: validContactList
    })
  })

  return validFirms?.filter((x) => x?.contactList?.length > 0 && x?.website)
}

const getProcessedExcelData = (data) => {
  if (data?.length) {
    const keys = Object.keys(data[0])

    const rows = data.map((item) => ({
      companyName: item?.[keys[0]]?.toString().trim() || '',
      website: item?.[keys[1]]?.toString().trim() || '',
      firstName: item?.[keys[2]]?.toString().trim() || '',
      lastName: item?.[keys[3]]?.toString().trim() || '',
      email: item?.[keys[4]]?.toString().trim() || ''
    }))

    return rows
  } else {
    return []
  }
}

const UploadExcelFirmListModalBtn = (props) => {
  const { excelFirms, setExcelFirms } = props

  const { t } = useTranslation()

  const languageGSD = useGlobalStoreData.language()

  const [open, setOpen] = useState(false)
  const [excelData, setExcelData] = useState([])

  const excelFilePathName = languageGSD === LANGUAGES.TURKISH ? 'Şablon.xlsx' : 'Template.xlsx'

  const uploadInputRef = useRef()
  const downloadTemplateRef = useRef()

  const handleClose = () => {
    setOpen(false)
  }

  const handleSubmit = () => {
    const cleanedData = getValidExcelFirms(excelData)
    setExcelFirms(structuredClone(cleanedData))
    handleClose()
  }

  const handleRowChange = (index, field, value) => {
    setExcelData((prevState) => {
      const newState = [...prevState]
      newState[index] = { ...newState[index], [field]: value }
      return newState
    })
  }

  const handleDeleteRow = (index) => {
    setExcelData((prevState) => prevState.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (open) {
      setExcelData(transformToFlatRows(excelFirms))
    }
  }, [open])

  return (
    <>
      <IconButton
        onClick={() => { setOpen(true) }}
        color='primary'
      >
        <UploadFile/>
      </IconButton>

      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        maxWidth='lg'
        scroll='body'
        sx={{ '& .MuiDialog-paper': { overflow: 'visible' } }}
      >
        <DialogCloseButton onClick={handleClose} disableRipple>
          <Close />
        </DialogCloseButton>
        <DialogTitle
          variant='h4'
          className='flex gap-2 flex-col text-center sm:pbs-16 sm:pbe-6 sm:pli-16'
        >
          {t('mailTemplate.wizard.firmList.excelModal.title')}
          <Typography component='span' className='flex flex-col text-center'>
            {t('mailTemplate.wizard.firmList.excelModal.subTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent className='overflow-visible pbs-0 sm:pli-16'>
          <div className='flex gap-2 mb-4'>
            <Button
              variant='contained'
              onClick={() => { uploadInputRef.current.click() }}
              startIcon={<UploadFile/>}
              color='primary'
            >
              {t('mailTemplate.wizard.firmList.excelModal.excelUpload')}
            </Button>
            <Tooltip
              title={t('mailTemplate.wizard.firmList.excelModal.excelSampleTooltip')}
              arrow
            >
              <Button
                variant='contained'
                onClick={() => { downloadTemplateRef.current.click() }}
                startIcon={<Download/>}
                color='primary'
              >
                {t('mailTemplate.wizard.firmList.excelModal.excelSample')}
              </Button>
            </Tooltip>
          </div>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>
                    {t('mailTemplate.wizard.firmList.excelModal.companyName')}
                    <span style={{ color: 'var(--mui-palette-error-main)' }}> *</span>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
                    {t('mailTemplate.wizard.firmList.excelModal.website')}
                    <span style={{ color: 'var(--mui-palette-error-main)' }}> *</span>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                    {t('mailTemplate.wizard.firmList.excelModal.firstName')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                    {t('mailTemplate.wizard.firmList.excelModal.lastName')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>
                    {t('mailTemplate.wizard.firmList.excelModal.email')}
                    <span style={{ color: 'var(--mui-palette-error-main)' }}> *</span>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData?.map((row, index) => {
                  const isCompanyNameEmpty = !row.companyName?.trim()
                  const isWebsiteEmpty = !row.website?.trim()
                  const isEmailEmpty = !row.email?.trim()

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={row.companyName}
                          onChange={(e) => handleRowChange(index, 'companyName', e.target.value)}
                          variant='standard'
                          error={isCompanyNameEmpty}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={row.website}
                          onChange={(e) => handleRowChange(index, 'website', e.target.value)}
                          variant='standard'
                          error={isWebsiteEmpty}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={row.firstName}
                          onChange={(e) => handleRowChange(index, 'firstName', e.target.value)}
                          variant='standard'
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={row.lastName}
                          onChange={(e) => handleRowChange(index, 'lastName', e.target.value)}
                          variant='standard'
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          value={row.email}
                          onChange={(e) => handleRowChange(index, 'email', e.target.value)}
                          variant='standard'
                          error={isEmailEmpty}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() => handleDeleteRow(index)}
                          color='error'
                        >
                          <Delete fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {excelData?.length === 0 && (
            <Typography
              variant='body2'
              color='textSecondary'
              className='text-center py-8'
            >
              {t('mailTemplate.wizard.firmList.excelModal.emptyTableMessage')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions className='justify-center pbs-0 sm:pbe-16 sm:pli-16'>
          <div className='flex flex-col items-center gap-2 w-full'>
            <div className='flex gap-4'>
              <Button
                variant='contained'
                onClick={handleSubmit}
                disabled={excelData?.length === 0}
              >
                {t('mailTemplate.wizard.firmList.excelModal.submit')}
              </Button>
              <Button
                variant='tonal'
                color='secondary'
                onClick={handleClose}
              >
                {t('mailTemplate.wizard.firmList.excelModal.cancel')}
              </Button>
            </div>
            <span
              style={{
                fontSize: '.6rem',
                color: 'var(--mui-palette-text-secondary)'
              }}
            >
              {t('mailTemplate.wizard.firmList.excelModal.submitWarning')}
            </span>
          </div>
        </DialogActions>
      </Dialog>

      <a
        ref={downloadTemplateRef}
        href={`${process.env.PUBLIC_URL}/assets/mailTemplate/${excelFilePathName}`}
        download={excelFilePathName}
        style={{ display: 'none' }}
      />
      <input
        ref={uploadInputRef}
        type="file"
        onChange={(e) => {
          const file = e.target.files[0]

          readExcel(file).then((result) => {
            const processedData = getProcessedExcelData(result)
            setExcelData(processedData)
          })
        }}
        style={{ display: 'none' }}
      />
    </>
  )
}

export default UploadExcelFirmListModalBtn
